import React from 'react'
import './job-schedule-editor.scss'
import clone from 'clone-deep'
import classnames from 'classnames'
import { parseExpression } from 'cron-parser'
import { Drawer } from '@pushly/aqe/lib/components'
import { useRefState } from '@halo/hooks/use-ref-state'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input, notification, Switch } from 'antd'
import { useDebouncer } from '@halo/hooks/use-debouncer'

interface IJobScheduleEditor {
    className?: string
    mode?: 'edit' | 'create'
    schedule?: any
    visible?: boolean
    submitText?: string
    onSubmit?: (schedule: any) => void
    onClose?: () => void
    onVisibilityChange?: (visible?: boolean) => void
}

interface IValidationResponse {
    ok: boolean
    errors: string[]
}
const setNotOk = (validation: IValidationResponse, msg: string) => {
    validation.ok = false
    validation.errors.push(msg)
}

const toStr = (param: any): undefined | string => {
    let str: string
    if (!!param) {
        str = (typeof param === 'string' ? param : JSON.stringify(param)).trim()
    }
    return str
}

const validateSubmit = (schedule: any): IValidationResponse => {
    const res: IValidationResponse = { ok: true, errors: [] }
    const targetStr = toStr(schedule.target)
    const payloadStr = toStr(schedule.payload)
    const alarmStr = toStr(schedule.alarm_config)

    if (!schedule.name?.trim()) {
        setNotOk(res, 'Name is required.')
    }

    if (!schedule.schedule?.trim()) {
        setNotOk(res, 'Schedule is required.')
    } else {
        const parts = schedule.schedule.trim().split(/\s+/g)
        if (parts.length < 5 || parts.length > 6) {
            setNotOk(res, 'Invalid cron expression specified.')
        } else {
            try {
                parseExpression(schedule.schedule.trim())
            } catch (_) {
                setNotOk(res, 'Invalid cron expression specified.')
            }
        }
    }

    if (!targetStr) {
        setNotOk(res, 'Target is required.')
    } else {
        let json: any = {}
        try {
            json = JSON.parse(targetStr)
        } catch (_) {
            setNotOk(res, 'Invalid target json specified.')
        }

        if (!json.type) {
            setNotOk(res, 'Target type is required.')
        }
        if (!json.name) {
            setNotOk(res, 'Target name is required.')
        }
    }

    if (!!payloadStr) {
        try {
            JSON.parse(payloadStr)
        } catch (_) {
            setNotOk(res, 'Invalid payload json specified.')
        }
    }

    if (!!alarmStr) {
        try {
            JSON.parse(alarmStr)
        } catch (_) {
            setNotOk(res, 'Invalid alarm config json specified.')
        }
    }

    return res
}

const JobScheduleEditor = (props: IJobScheduleEditor) => {
    const mode = props.mode ?? 'create'
    const isCreateMode = mode === 'create'
    const hasSchedule = !!props.schedule

    const [tmpValue, setTmpValue] = useRefState<any>(
        props.schedule ?? {
            enabled: true,
            prod_only: false,
        },
    )
    const [debounce] = useDebouncer()
    const value = clone(tmpValue.current)
    return (
        <Drawer
            className={classnames('job-schedule-editor', `mode-${mode}`, props.className, {
                visible: props.visible,
            })}
            title={`${isCreateMode ? 'Create' : 'Edit'} Job Schedule`}
            visible={props.visible ?? false}
            afterVisibleChange={props.onVisibilityChange}
            submitText={props.submitText ?? (isCreateMode ? 'Submit' : 'Save')}
            onClose={() => props.onClose?.()}
            onSubmit={() => {
                const dto = clone(tmpValue.current)
                const res = validateSubmit(dto)

                if (res.ok) {
                    dto.name = dto.name.trim()
                    dto.schedule = dto.schedule.trim()
                    dto.target = JSON.parse(toStr(dto.target))
                    dto.payload = !dto.payload ? null : JSON.parse(toStr(dto.payload))
                    dto.alarm_config = !dto.alarm_config ? null : JSON.parse(toStr(dto.alarm_config))

                    return props.onSubmit?.(dto)
                } else {
                    notification.destroy()
                    notification.error({
                        message: '',
                        description: res.errors.map((err, idx) => (
                            <div key={idx}>
                                <span>{err}</span>
                            </div>
                        )),
                    })
                }
            }}
        >
            <Form layout="horizontal">
                <Form.Item label="Name">
                    <Input
                        defaultValue={value.name}
                        onChange={(ev) => {
                            const name = ev.target.value
                            debounce(() => {
                                setTmpValue((prev: any) => ({
                                    ...prev,
                                    name,
                                }))
                            })
                        }}
                    />
                </Form.Item>
                <div className="switch-row">
                    <div className="switch-left">
                        <Switch
                            size="small"
                            checked={value.enabled}
                            onChange={(v) => setTmpValue((prev: any) => ({ ...prev, enabled: v }))}
                        />
                    </div>
                    <div className="switch-right">Enabled</div>
                </div>
                <div className="switch-row">
                    <div className="switch-left">
                        <Switch
                            size="small"
                            checked={value.prod_only}
                            onChange={(v) => setTmpValue((prev: any) => ({ ...prev, prod_only: v }))}
                        />
                    </div>
                    <div className="switch-right">Production Env Only</div>
                </div>
                <Form.Item label="Schedule - Standard CRON Format (* * * * *)">
                    <Input
                        defaultValue={value.schedule}
                        onChange={(ev) => {
                            const schedule = ev.target.value
                            debounce(() => {
                                setTmpValue((prev: any) => ({
                                    ...prev,
                                    schedule,
                                }))
                            })
                        }}
                    />
                </Form.Item>
                <Form.Item label="Target (JSON String)">
                    <Input
                        defaultValue={!value.target ? undefined : toStr(value.target)}
                        onChange={(ev) => {
                            const target = ev.target.value
                            debounce(() => {
                                setTmpValue((prev: any) => ({
                                    ...prev,
                                    target,
                                }))
                            })
                        }}
                    />
                </Form.Item>
                <Form.Item label="Payload (JSON String)">
                    <Input
                        defaultValue={!value.payload ? undefined : toStr(value.payload)}
                        onChange={(ev) => {
                            const payload = ev.target.value
                            debounce(() => {
                                setTmpValue((prev: any) => ({
                                    ...prev,
                                    payload,
                                }))
                            })
                        }}
                    />
                </Form.Item>
                <Form.Item label="Alarm Config (JSON String)">
                    <Input
                        defaultValue={!value.alarm_config ? undefined : toStr(value.alarm_config)}
                        onChange={(ev) => {
                            const alarm_config = ev.target.value
                            debounce(() => {
                                setTmpValue((prev: any) => ({
                                    ...prev,
                                    alarm_config,
                                }))
                            })
                        }}
                    />
                </Form.Item>
            </Form>
        </Drawer>
    )
}

export default JobScheduleEditor
